/**
 * Tag Component
 * Base tag component with semantic variants
 */

import React from "react";
import { Tag as ChakraTag, TagLabel, TagCloseButton, TagProps as ChakraTagProps } from "@chakra-ui/react";
import { useColorModeValue } from "design-system/hooks/useColorModeValue";
import CDS from "design-system";

// Branded types for Tag variants
declare const __tag_variant_brand: unique symbol;
declare const __tag_size_brand: unique symbol;

export interface TagVariantToken {
  readonly [__tag_variant_brand]: true;
  readonly _value: string;
}

export interface TagSizeToken {
  readonly [__tag_size_brand]: true;
  readonly _value: string;
}

// Factory functions
const createTagVariantToken = (value: string): TagVariantToken => ({ _value: value } as TagVariantToken);

const createTagSizeToken = (value: string): TagSizeToken => ({ _value: value } as TagSizeToken);

export interface TagProps extends Omit<ChakraTagProps, "variant" | "size"> {
  children: React.ReactNode;
  variant?: TagVariantToken;
  size?: TagSizeToken;
  closable?: boolean;
  onClose?: () => void;
}

function TagComponent({
  children,
  variant = tagTokens.variant.neutral,
  size = tagTokens.size.medium,
  closable = false,
  onClose,
  ...props
}: TagProps) {
  // Pre-compute all color values using Camino brand colors
  const neutralBg = useColorModeValue(CDS.Color.primitive.gray(100));
  const neutralColor = useColorModeValue(CDS.Color.primitive.gray(700));

  const primaryBg = useColorModeValue(CDS.Color.primitive.brand(100));
  const primaryColor = useColorModeValue(CDS.Color.primitive.brand(700));

  const successBg = useColorModeValue(CDS.Color.primitive.green(100));
  const successColor = useColorModeValue(CDS.Color.primitive.green(700));

  const warningBg = useColorModeValue(CDS.Color.primitive.orange(100));
  const warningColor = useColorModeValue(CDS.Color.primitive.orange(700));

  const errorBg = useColorModeValue(CDS.Color.primitive.red(100));
  const errorColor = useColorModeValue(CDS.Color.primitive.red(700));

  const getVariantStyles = (variant: TagVariantToken) => {
    switch (variant._value) {
      case "primary":
        return {
          bg: primaryBg,
          color: primaryColor,
        };
      case "success":
        return {
          bg: successBg,
          color: successColor,
        };
      case "warning":
        return {
          bg: warningBg,
          color: warningColor,
        };
      case "error":
        return {
          bg: errorBg,
          color: errorColor,
        };
      case "neutral":
      default:
        return {
          bg: neutralBg,
          color: neutralColor,
        };
    }
  };

  const getSizeProps = (size: TagSizeToken) => {
    switch (size._value) {
      case "small":
        return { size: "sm" as const };
      case "medium":
        return { size: "md" as const };
      case "large":
        return { size: "lg" as const };
      default:
        return { size: "md" as const };
    }
  };

  const variantStyles = getVariantStyles(variant);
  const sizeProps = getSizeProps(size);

  return (
    <ChakraTag
      variant="solid"
      {...sizeProps}
      {...variantStyles}
      {...props}
    >
      <TagLabel fontWeight="medium">
        {children}
      </TagLabel>
      {closable && <TagCloseButton onClick={onClose} />}
    </ChakraTag>
  );
}

// Tag tokens
const tagTokens = {
  variant: {
    neutral: createTagVariantToken("neutral"),
    primary: createTagVariantToken("primary"),
    success: createTagVariantToken("success"),
    warning: createTagVariantToken("warning"),
    error: createTagVariantToken("error"),
  } as const,

  size: {
    small: createTagSizeToken("small"),
    medium: createTagSizeToken("medium"),
    large: createTagSizeToken("large"),
  } as const,
} as const;

// Public API - combines tokens and component
export const Tag = Object.assign(TagComponent, tagTokens);
